import Layout from "@/components/Layout";
import SEOHead from "@/components/SEOHead";
import PageHero from "@/components/PageHero";
import { CONTACT } from "@/lib/constants";

export default function RedaktionellPolicy() {
  return (
    <Layout>
      <SEOHead
        title="Redaktionell policy | ASPAB"
        description="Läs om ASPAB:s redaktionella riktlinjer för webbplatsens innehåll. Hur vi säkerställer korrekt, aktuell och trovärdig information om laserskärning och metallbearbetning."
        canonical="https://aspab.com/redaktionell-policy"
        path="/redaktionell-policy"
        breadcrumbs={[
          { name: "Policyer och villkor", href: "/policyer" },
          { name: "Redaktionell policy", href: "/redaktionell-policy" },
        ]}

        ogImage="/images/og-redaktionell-policy_2670a626.png"
      />

      <PageHero
        title="Redaktionell policy"
        subtitle="Så säkerställer vi korrekt och trovärdig information på aspab.com."
        breadcrumbs={[
          { name: "Policyer och villkor", href: "/policyer" },
          { name: "Redaktionell policy", href: "/redaktionell-policy" },
        ]}
      />

      <section className="bg-white py-20 md:py-28">
        <div className="container">
          <div className="max-w-3xl mx-auto">
            <p className="text-gray-500 text-sm mb-10">
              Senast uppdaterad: 18 september 2026
            </p>

            <div className="space-y-12">

              {/* 1. Syfte */}
              <div>
                <h2 className="font-heading text-2xl font-bold text-gray-900">
                  1. Syfte och omfattning
                </h2>
                <p className="text-gray-600 mt-4 leading-relaxed">
                  Denna redaktionella policy beskriver de principer och riktlinjer som styr
                  allt innehåll på aspab.com. Policyn gäller för alla sidor, artiklar,
                  tekniska specifikationer och övrigt material som publiceras på webbplatsen.
                  Syftet är att säkerställa att besökare alltid kan lita på att informationen
                  är korrekt, aktuell och relevant.
                </p>
              </div>

              {/* 2. Redaktionella principer */}
              <div>
                <h2 className="font-heading text-2xl font-bold text-gray-900">
                  2. Redaktionella principer
                </h2>
                <p className="text-gray-600 mt-4 leading-relaxed">
                  Allt innehåll på aspab.com baseras på följande principer:
                </p>
                <div className="mt-4 space-y-4">
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-5">
                    <h3 className="font-heading font-semibold text-gray-900 mb-2">Saklighet och korrekthet</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      All teknisk information, specifikationer och kapacitetsuppgifter baseras på
                      faktiska maskindata, tillverkarens dokumentation och vår egen erfarenhet
                      från över 30 års verksamhet. Vi anger aldrig kapacitet eller toleranser som
                      vi inte kan uppfylla i praktiken.
                    </p>
                  </div>
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-5">
                    <h3 className="font-heading font-semibold text-gray-900 mb-2">Transparens</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      Vi är tydliga med vad vi kan och inte kan erbjuda. Om en tjänst har
                      begränsningar – exempelvis maximala materialtjocklekar eller leveranstider
                      som varierar beroende på komplexitet – kommunicerar vi detta öppet.
                    </p>
                  </div>
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-5">
                    <h3 className="font-heading font-semibold text-gray-900 mb-2">Aktualitet</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      Vi granskar och uppdaterar webbplatsens innehåll löpande för att
                      säkerställa att information om maskinpark, tjänster och kapacitet
                      speglar aktuellt läge. Varje sida anger senast uppdaterat datum där
                      det är relevant.
                    </p>
                  </div>
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-5">
                    <h3 className="font-heading font-semibold text-gray-900 mb-2">Oberoende</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      Innehållet på aspab.com produceras av ASPAB och är inte sponsrat av eller
                      beroende av externa parter. När vi nämner specifika varumärken,
                      maskintillverkare eller materialleverantörer gör vi det baserat på vår
                      faktiska erfarenhet och användning, inte på kommersiella avtal.
                    </p>
                  </div>
                </div>
              </div>

              {/* 3. Författare och expertis */}
              <div>
                <h2 className="font-heading text-2xl font-bold text-gray-900">
                  3. Författare och expertis
                </h2>
                <p className="text-gray-600 mt-4 leading-relaxed">
                  Innehållet på aspab.com produceras av ASPAB:s team, som har mångårig
                  erfarenhet inom metallbearbetning, laserskärning, kantpressning och
                  svetsning. Tekniska artiklar och specifikationer granskas av produktionsledning
                  och maskinoperatörer med praktisk erfarenhet av de processer som beskrivs.
                </p>
                <p className="text-gray-600 mt-3 leading-relaxed">
                  Robin Fischer, ägare och VD för ASPAB sedan augusti 2020, ansvarar för att publicerat innehåll överensstämmer med företagets faktiska kapacitet och kompetens.
                </p>
              </div>

              {/* 4. Granskningsprocess */}
              <div>
                <h2 className="font-heading text-2xl font-bold text-gray-900">
                  4. Granskningsprocess
                </h2>
                <p className="text-gray-600 mt-4 leading-relaxed">
                  Innan nytt innehåll publiceras på aspab.com genomgår det följande steg:
                </p>
                <ol className="mt-4 space-y-3 text-gray-600 leading-relaxed">
                  <li className="flex gap-3">
                    <span className="font-heading font-bold text-[#2563EB] shrink-0">1.</span>
                    <span><strong className="text-gray-900">Faktakontroll</strong> – Tekniska uppgifter verifieras mot maskinspecifikationer, tillverkarens dokumentation och intern produktionsdata.</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="font-heading font-bold text-[#2563EB] shrink-0">2.</span>
                    <span><strong className="text-gray-900">Expertgranskning</strong> – Innehåll som rör specifika bearbetningsmetoder granskas av operatörer eller produktionsledning med praktisk erfarenhet.</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="font-heading font-bold text-[#2563EB] shrink-0">3.</span>
                    <span><strong className="text-gray-900">Slutgodkännande</strong> – VD eller ansvarig chef godkänner innehållet innan publicering.</span>
                  </li>
                </ol>
              </div>

              {/* 5. Nyheter och artiklar */}
              <div>
                <h2 className="font-heading text-2xl font-bold text-gray-900">
                  5. Nyheter och artiklar
                </h2>
                <p className="text-gray-600 mt-4 leading-relaxed">
                  Artiklar i vår nyhetsdel fokuserar på investeringar, teknikutveckling och
                  branschrelevanta ämnen som rör ASPAB:s verksamhet. Varje artikel anger
                  publiceringsdatum och uppdateras vid behov med korrigeringar eller
                  kompletteringar.
                </p>
                <p className="text-gray-600 mt-3 leading-relaxed">
                  Vi strävar efter att ge läsaren praktisk och användbar information som
                  hjälper till att förstå våra processer och hur de kan gynna deras projekt.
                  Artiklar skrivs med fokus på att förklara tekniska koncept på ett
                  tillgängligt sätt utan att kompromissa med sakligheten.
                </p>
              </div>

              {/* 6. Referenser och kundcase */}
              <div>
                <h2 className="font-heading text-2xl font-bold text-gray-900">
                  6. Referenser och kundcase
                </h2>
                <p className="text-gray-600 mt-4 leading-relaxed">
                  Kundcase och referenser som publiceras på aspab.com baseras på faktiska
                  samarbeten och projekt. Vi publicerar aldrig kundnamn eller projektdetaljer
                  utan kundens uttryckliga medgivande. Beskrivningar av projekt och resultat
                  är sanningsenliga och verifierbara.
                </p>
              </div>

              {/* 7. Bilder och media */}
              <div>
                <h2 className="font-heading text-2xl font-bold text-gray-900">
                  7. Bilder och media
                </h2>
                <p className="text-gray-600 mt-4 leading-relaxed">
                  Bilder på aspab.com visar ASPAB:s faktiska anläggning, maskiner, produkter
                  och personal, om inte annat anges. Vi använder inte stockbilder för att
                  representera vår verksamhet eller våra produkter. Produktbilder visar
                  faktiska resultat från vår produktion.
                </p>
              </div>

              {/* 8. Korrigeringar */}
              <div>
                <h2 className="font-heading text-2xl font-bold text-gray-900">
                  8. Korrigeringar och uppdateringar
                </h2>
                <p className="text-gray-600 mt-4 leading-relaxed">
                  Om vi upptäcker fel i publicerat innehåll korrigerar vi det så snart som
                  möjligt. Vid väsentliga korrigeringar av artiklar anger vi att innehållet
                  har uppdaterats och vad som har ändrats. Mindre redaktionella justeringar
                  (stavning, formatering) görs utan särskild notering.
                </p>
                <p className="text-gray-600 mt-3 leading-relaxed">
                  Om du som besökare upptäcker felaktig information på vår webbplats uppskattar
                  vi om du kontaktar oss så att vi kan granska och korrigera uppgifterna.
                </p>
              </div>

              {/* 9. Kontakt */}
              <div>
                <h2 className="font-heading text-2xl font-bold text-gray-900">
                  9. Kontakt
                </h2>
                <p className="text-gray-600 mt-4 leading-relaxed">
                  Har du frågor om vårt redaktionella arbete eller vill rapportera felaktig
                  information? Kontakta oss:
                </p>
                <div className="mt-4 bg-gray-50 border border-gray-200 rounded-lg p-5">
                  <div className="text-gray-600 text-sm space-y-1">
                    <p className="font-medium text-gray-700">ASPAB</p>
                    <p>{CONTACT.address}, {CONTACT.city}</p>
                    <p>E-post: <a href={`mailto:${CONTACT.email}`} className="text-[#2563EB] hover:underline">{CONTACT.email}</a></p>
                    <p>Telefon: <a href={`tel:${CONTACT.phone.replace(/[^0-9+]/g, "")}`} className="text-[#2563EB] hover:underline">{CONTACT.phone}</a></p>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
